/**
 * Maps the authoritative server `RoomSnapshot` into the UI shape pages already expect.
 *
 * Only the viewer's hand is ever copied from the snapshot. Submission player
 * identities are omitted during judging (revealIndex is used instead).
 */

import type { RoomSnapshot } from '@deck-after-dark/shared';
import type {
  GamePhase,
  GamePlayer,
  HandCard,
  MockGameState,
  RoundResult,
  Submission,
  WinLogEntry,
} from './mockGameTypes';
import { DEFAULT_SETTINGS } from './mockGameTypes';
import { getInitials, gradientForIndex } from './playerUtils';

function mapPhase(snapshot: RoomSnapshot): GamePhase {
  if (!snapshot.started || !snapshot.game) return 'lobby';
  switch (snapshot.game.phase) {
    case 'submitting':
      return 'playing';
    case 'judging':
      return 'judging';
    case 'roundResult':
      return 'roundResult';
    case 'gameOver':
      return 'gameOver';
    default:
      return 'lobby';
  }
}

function mapPlayers(snapshot: RoomSnapshot): GamePlayer[] {
  const roster = snapshot.started && snapshot.game
    ? snapshot.game.players
    : snapshot.lobby.players.map((p) => ({
        id: p.id,
        name: p.name,
        isActive: p.isActive,
        isHost: p.isHost,
        isReady: p.isReady,
        score: 0,
        isJudge: false,
        hasSubmitted: false,
      }));

  return roster.map((p, index) => ({
    id: p.id,
    name: p.name,
    initials: getInitials(p.name),
    score: 'score' in p ? p.score : 0,
    isHost: p.isHost,
    isReady: 'isReady' in p ? p.isReady : false,
    isConnected: snapshot.lobby.players.find((seat) => seat.id === p.id)?.isActive ?? p.isActive,
    gradient: gradientForIndex(index),
  }));
}

function mapHand(snapshot: RoomSnapshot): HandCard[] {
  if (!snapshot.game) return [];
  return snapshot.game.hand.map((card) => ({
    id: card.id,
    text: card.text,
    type: card.isWriteIn ? ('wild' as const) : ('white' as const),
  }));
}

function mapSubmissions(snapshot: RoomSnapshot): Submission[] {
  const round = snapshot.game?.round;
  if (!round || round.submissions.length === 0) return [];

  return round.submissions.map((sub) => ({
    id: `reveal-${sub.index}`,
    playerId: `reveal-${sub.index}`,
    text: sub.cards.map((c) => c.text).join(' / '),
    revealIndex: sub.index,
  }));
}

function mapLastRoundResult(snapshot: RoomSnapshot): RoundResult | null {
  const game = snapshot.game;
  const round = game?.round;
  if (!game || !round) return null;

  if (round.winnerId && round.winningSubmission) {
    return {
      winnerId: round.winnerId,
      answerText: round.winningSubmission.cards.map((c) => c.text).join(' / '),
      promptText: round.blackCard.text,
    };
  }

  const last = game.history[game.history.length - 1];
  if (!last) return null;
  return {
    winnerId: last.winnerId,
    answerText: last.winningSubmission.cards.map((c) => c.text).join(' / '),
    promptText: last.blackCard.text,
  };
}

function mapRoundHistory(snapshot: RoomSnapshot): WinLogEntry[] {
  const game = snapshot.game;
  if (!game) return [];
  const nameFor = (id: string) =>
    game.players.find((p) => p.id === id)?.name ?? 'Unknown';
  return game.history.map((result) => ({
    roundNumber: result.roundNumber,
    promptText: result.blackCard.text,
    answerText: result.winningSubmission.cards.map((c) => c.text).join(' / '),
    winnerId: result.winnerId,
    winnerName: nameFor(result.winnerId),
  }));
}

export interface SnapshotAdapterInput {
  snapshot: RoomSnapshot;
  /** Local UI-only ordered selection (never authoritative). */
  selectedCardIds?: readonly string[];
  writeInText?: string;
  joinError?: string | null;
}

export function snapshotToGameState(input: SnapshotAdapterInput): MockGameState {
  const { snapshot } = input;
  const phase = mapPhase(snapshot);
  const players = mapPlayers(snapshot);
  const round = snapshot.game?.round ?? null;

  return {
    roomCode: snapshot.roomCode,
    currentPlayerId: snapshot.you,
    players,
    judgeId: round?.judgeId ?? null,
    settings: {
      roomName: snapshot.settings.deckId,
      maxPlayers: snapshot.settings.maxPlayers,
      pointsToWin: snapshot.settings.pointsToWin,
      roundTimer: Boolean(snapshot.settings.roundTimerSeconds),
      wildCardsEnabled: snapshot.settings.allowWriteIns,
      privateRoom: true,
    },
    phase,
    roundNumber: round?.roundNumber ?? 0,
    maxRounds: snapshot.settings.pointsToWin,
    blackCardText: round?.blackCard.text ?? null,
    blackCardPick: round?.pick ?? 1,
    hand: mapHand(snapshot),
    selectedCardIds: [...(input.selectedCardIds ?? [])],
    selectedCardId: input.selectedCardIds?.[0] ?? null,
    writeInText: input.writeInText ?? '',
    submissions: mapSubmissions(snapshot),
    lastRoundResult: mapLastRoundResult(snapshot),
    roundHistory: mapRoundHistory(snapshot),
    submittedCount: round?.submissionCount ?? 0,
    log: [],
    joinError: input.joinError ?? null,
    canStartGame: snapshot.lobby.canStart,
    canSubmit: snapshot.game?.you.canSubmit ?? false,
    canPickWinner: snapshot.game?.you.canPickWinner ?? false,
    canAdvance: snapshot.game?.you.canAdvance ?? false,
    viewerHasSubmitted: snapshot.game?.you.hasSubmitted ?? false,
    writeInMaxLength: snapshot.settings.writeInMaxLength,
    ...(snapshot.game?.endedReason ? { endedReason: snapshot.game.endedReason } : {}),
    joinedMidRound: snapshot.game?.you.joinedMidRound ?? false,
  };
}

export const EMPTY_SERVER_UI_STATE: MockGameState = {
  roomCode: null,
  currentPlayerId: null,
  players: [],
  judgeId: null,
  settings: DEFAULT_SETTINGS,
  phase: 'none',
  roundNumber: 0,
  maxRounds: 15,
  blackCardText: null,
  blackCardPick: 1,
  hand: [],
  selectedCardIds: [],
  selectedCardId: null,
  writeInText: '',
  submissions: [],
  lastRoundResult: null,
  roundHistory: [],
  log: [],
  joinError: null,
};

export function mapClientSettingsToServer(settings: Partial<MockGameState['settings']>) {
  return {
    maxPlayers: settings.maxPlayers,
    pointsToWin: settings.pointsToWin,
    allowWriteIns: settings.wildCardsEnabled,
    writeInCount: settings.wildCardsEnabled ? 1 : 0,
  };
}
